/*
  Dot field — a fine grid of dots that parts around the pointer.

  Behaviour (tuned against the reference, implemented from scratch):
  - The pointer is a soft source: dots inside its radius are pushed out and
    pile into a ring at the edge, like a finger pressed into sand.
    Displacement = sqrt(r² + a²) − r, which opens a clean hole of radius `a`
    and fades smoothly with distance.
  - The hole is slightly diamond-shaped (a blend of round and L1 distance),
    so the grid reads as structure rather than as a spotlight.
  - Arriving with some speed opens the hole wider (~1s to fully open); if the
    pointer then rests, it slowly settles to a smaller steady hole (~10s).
  - The pointer position is eased, and each dot eases toward its target, so
    the response trails the cursor slightly and heals ~0.8s after it leaves.
  - No idle motion. When nothing is moving the loop stops entirely.
  - Touch devices and reduced-motion get the static grid only.
*/
export function initializeHome(root) {
  var host = root.querySelector('.field');
  var canvas = host && host.querySelector('canvas');
  if (!canvas || !canvas.getContext) return;
  let disposed = false;
  var ctx = canvas.getContext('2d');
  if (!ctx) return;

  var reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  var finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');

  // ---- Tunables (CSS px / seconds) ----
  var DOT = 1.6;            // dot size
  var RADIUS = 92;          // hole radius at full strength
  var REACH = 4.2;          // influence cut-off, in radii
  var POINTER_TAU = 0.085;  // pointer easing time constant
  var DOT_RATE = 14;        // how quickly dots follow their target (1/s)
  var OPEN_RATE = 7;        // strength rise (1/s)
  var CLOSE_RATE = 4;       // strength fall after leaving (1/s)
  var REST_LEVEL = 0.63;    // steady strength when the pointer rests
  var ENERGY_DECAY = 5;     // seconds for arrival energy to fade
  var INK = '30,20,30';     // #1E141E
  var ALPHA = 0.16;         // peak dot opacity (before page-level fade)
  var BUCKETS = 6;          // opacity levels, batched per fill

  var W = 0, H = 0, dpr = 1, pitch = 12;
  var n = 0, hx, hy, ox, oy, bucket;
  var rect = null;

  var target = { x: 0, y: 0 }, ptr = { x: 0, y: 0 }, last = { x: 0, y: 0 };
  var inside = false, hasPtr = false, strength = 0, energy = 0;
  var raf = 0, running = false, prevT = 0;

  function interactive() { return finePointer.matches && !reduce.matches; }

  // Smooth low-frequency variation so the field has soft, uneven density.
  function patch(x, y) {
    var v = Math.sin(x * 0.0061 + y * 0.0023) * 0.5
          + Math.sin(x * 0.0017 - y * 0.0071 + 1.7) * 0.35
          + Math.sin((x + y) * 0.0034 + 4.1) * 0.25;
    return 0.62 + 0.38 * (v / 1.1 * 0.5 + 0.5);
  }

  // The field covers the hero, then runs a little past it so the mask can fade it
  // out as the case studies begin. Below that the page is plain background.
  var FADE_PAST_HERO = 300;
  function sizeHost() {
    var hero = root.querySelector('.hero');
    if (!hero) return;
    host.style.bottom = 'auto';
    host.style.height = (hero.offsetTop + hero.offsetHeight + FADE_PAST_HERO) + 'px';
  }

  function build() {
    sizeHost();
    rect = host.getBoundingClientRect();
    W = rect.width; H = rect.height;
    dpr = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = Math.round(W * dpr);
    canvas.height = Math.round(H * dpr);
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    pitch = W < 700 ? 14 : 12;
    var cols = Math.ceil(W / pitch) + 1, rows = Math.ceil(H / pitch) + 1;
    var offX = (W - (cols - 1) * pitch) / 2, offY = (H - (rows - 1) * pitch) / 2;
    n = cols * rows;
    hx = new Float32Array(n); hy = new Float32Array(n);
    ox = new Float32Array(n); oy = new Float32Array(n);
    bucket = new Uint8Array(n);
    var i = 0;
    for (var r = 0; r < rows; r++) {
      for (var c = 0; c < cols; c++, i++) {
        hx[i] = offX + c * pitch;
        hy[i] = offY + r * pitch;
        bucket[i] = Math.min(BUCKETS - 1, Math.floor(patch(hx[i], hy[i]) * BUCKETS));
      }
    }
    draw();
  }

  function draw() {
    ctx.clearRect(0, 0, W, H);
    for (var b = 0; b < BUCKETS; b++) {
      ctx.beginPath();
      for (var i = 0; i < n; i++) {
        if (bucket[i] === b) ctx.rect(hx[i] + ox[i] - DOT / 2, hy[i] + oy[i] - DOT / 2, DOT, DOT);
      }
      ctx.fillStyle = 'rgba(' + INK + ',' + (ALPHA * (b + 1) / BUCKETS).toFixed(3) + ')';
      ctx.fill();
    }
  }

  function step(t) {
    raf = 0;
    // rAF timestamps can precede performance.now() at wake-up; never let dt go negative.
    var dt = Math.max(0.001, Math.min(0.05, (t - prevT) / 1000 || 0));
    prevT = t;

    // Ease the pointer; measure its speed for the arrival energy.
    var k = 1 - Math.exp(-dt / POINTER_TAU);
    ptr.x += (target.x - ptr.x) * k;
    ptr.y += (target.y - ptr.y) * k;
    var speed = Math.hypot(ptr.x - last.x, ptr.y - last.y) / dt;
    last.x = ptr.x; last.y = ptr.y;
    energy = Math.min(1, energy * Math.exp(-dt / ENERGY_DECAY) + speed * dt / 150);

    var goal = inside ? REST_LEVEL + (1 - REST_LEVEL) * energy : 0;
    var rate = goal > strength ? OPEN_RATE : CLOSE_RATE;
    strength += (goal - strength) * (1 - Math.exp(-dt * rate));
    if (strength < 0.0005 && !inside) strength = 0;

    var a = RADIUS * strength, a2 = a * a;
    var reach = RADIUS * REACH, reach2 = reach * reach, fall = 1 / (reach * 0.55 * reach * 0.55);
    var follow = 1 - Math.exp(-dt * DOT_RATE);
    var moving = Math.abs(goal - strength) > 0.002 || Math.abs(target.x - ptr.x) + Math.abs(target.y - ptr.y) > 0.1;

    for (var i = 0; i < n; i++) {
      var tx = 0, ty = 0;
      if (a > 0.01) {
        var dx = hx[i] - ptr.x, dy = hy[i] - ptr.y, d2 = dx * dx + dy * dy;
        if (d2 < reach2) {
          var re = Math.sqrt(d2) || 0.001;
          var rm = 0.35 * re + 0.5 * (Math.abs(dx) + Math.abs(dy)) || 0.001;
          var push = (Math.sqrt(rm * rm + a2) - rm) / rm * Math.exp(-d2 * fall);
          tx = dx * push; ty = dy * push;
        }
      }
      var ex = tx - ox[i], ey = ty - oy[i];
      if (ex > 0.02 || ex < -0.02 || ey > 0.02 || ey < -0.02) {
        ox[i] += ex * follow; oy[i] += ey * follow; moving = true;
      } else { ox[i] = tx; oy[i] = ty; }
    }

    draw();
    if (moving && !document.hidden) raf = requestAnimationFrame(step);
    else running = false;
  }

  function wake() {
    if (running || !interactive() || document.hidden) return;
    running = true;
    prevT = performance.now();
    raf = requestAnimationFrame(step);
  }

  function onMove(e) {
    if (e.pointerType && e.pointerType !== 'mouse') return;
    if (!interactive()) return;
    if (!rect) rect = host.getBoundingClientRect();
    var x = e.clientX - rect.left, y = e.clientY - rect.top;
    target.x = x; target.y = y;
    if (!hasPtr) { ptr.x = last.x = x; ptr.y = last.y = y; hasPtr = true; }
    inside = y >= 0 && y <= H;
    wake();
  }
  function onLeave() { inside = false; hasPtr = false; wake(); }

  function reset() {
    cancelAnimationFrame(raf); running = false;
    strength = 0; energy = 0; inside = false; hasPtr = false;
    if (ox) { ox.fill(0); oy.fill(0); }
    draw();
  }

  var resizeTimer = 0;
  function onResize() {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(function () { reset(); build(); }, 120);
  }
  function onScroll() { rect = host.getBoundingClientRect(); }
  function onVisibility() { if (document.hidden) { cancelAnimationFrame(raf); running = false; } }
  function onPrefChange() { reset(); }
  window.addEventListener('resize', onResize, { passive: true });
  window.addEventListener('scroll', onScroll, { passive: true });
  document.addEventListener('pointermove', onMove, { passive: true });
  document.documentElement.addEventListener('mouseleave', onLeave);
  window.addEventListener('blur', onLeave);
  document.addEventListener('visibilitychange', onVisibility);
  reduce.addEventListener('change', onPrefChange);
  finePointer.addEventListener('change', onPrefChange);

  build();
  if (document.fonts && document.fonts.ready) document.fonts.ready.then(function () { if (!disposed) { reset(); build(); } });

  let observer;
  if ('IntersectionObserver' in window && !reduce.matches) {
    root.classList.add('reveal-on');
    observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) { entry.target.classList.add('is-in'); observer.unobserve(entry.target); }
      });
    }, { rootMargin: '0px 0px -12% 0px', threshold: 0.08 });
    root.querySelectorAll('.rv').forEach(function (element) { observer.observe(element); });
  }

  return function () {
    disposed = true;
    cancelAnimationFrame(raf);
    clearTimeout(resizeTimer);
    if (observer) observer.disconnect();
    root.classList.remove('reveal-on');
    window.removeEventListener('resize', onResize);
    window.removeEventListener('scroll', onScroll);
    document.removeEventListener('pointermove', onMove);
    document.documentElement.removeEventListener('mouseleave', onLeave);
    window.removeEventListener('blur', onLeave);
    document.removeEventListener('visibilitychange', onVisibility);
    reduce.removeEventListener('change', onPrefChange);
    finePointer.removeEventListener('change', onPrefChange);
  };
}
